<script lang="ts">
  import QRCode from 'qrcode';
  import {
    DECADES, MIN_PLAYERS, MAX_PLAYERS,
    type DisplayView, type Decade, type Mode,
  } from '@banger/shared';
  import { lastError, send } from '../lib/connection';
  import { ALBUM_DECADES_NOTE, albumNote, lobbyNote } from '../lib/lobbyNote';
  import { activatePlayback } from './playback';
  import { unlock as unlockSfx } from '../lib/sfx';
  import HowToPlayLink from '../shared/HowToPlayLink.svelte';
  import TvWindowButton from '../shared/TvWindowButton.svelte';
  import PlayerList from '../shared/PlayerList.svelte';

  let { view }: { view: DisplayView } = $props();

  const joinUrl = $derived(`${location.origin}/${view.code}`);
  let qr = $state('');
  $effect(() => {
    let live = true;
    QRCode.toDataURL(joinUrl, { margin: 4, width: 420, color: { dark: '#12062a', light: '#ffffff' } })
      .then((u) => { if (live) qr = u; })
      .catch(() => { /* text URL remains */ });
    return () => { live = false; };
  });

  // Optimistic copy so rapid clicks build on each other; the server view re-syncs it.
  // svelte-ignore state_referenced_locally
  let decades = $state<Decade[]>([...view.settings.decades]);
  $effect(() => { decades = [...view.settings.decades]; });
  const note = $derived(lobbyNote(view.active, decades));
  const albumBlock = $derived(albumNote(view.settings.mode, decades));
  const enough = $derived(view.players.length >= MIN_PLAYERS);
  const canStart = $derived(enough && !albumBlock);

  function setMode(mode: Mode) {
    send({ type: 'settings', settings: { mode } });
  }
  function toggleDecade(d: Decade) {
    const on = decades.includes(d);
    if (on && decades.length === 1) return; // at least one stays on
    const next = on ? decades.filter((x) => x !== d) : [...decades, d].sort((a, b) => a - b);
    decades = next;
    send({ type: 'settings', settings: { decades: next } });
  }
  function kick(id: string) { send({ type: 'kick', playerId: id }); }
  // Sticky until the next Start: the next view (≤5 s) clears lastError.
  let startError = $state('');
  $effect(() => {
    if ($lastError?.code === 'CATALOG_NOT_READY') startError = $lastError.message;
    else if ($lastError?.code === 'ALBUM_NEEDS_DECADES') startError = ALBUM_DECADES_NOTE;
  });
  let starting = $state(false);
  async function onStartClicked() {
    if (starting) return;
    starting = true;
    startError = '';
    unlockSfx(); // synchronously inside the click, before any await
    try {
      await activatePlayback(); // the click is the gesture browsers need before audio
      send({ type: 'start_match' });
    } finally {
      starting = false;
    }
  }
</script>

<main>
  <header><h1 class="wordmark">BANGER!</h1></header>
  <section class="join">
    <div class="codebox">
      <div class="label">Join at <strong>{location.host}</strong> with code</div>
      <div class="code neon">{view.code}</div>
      <div class="url">{joinUrl}</div>
    </div>
    <div class="qr">
      {#if qr}<img src={qr} alt="QR code to join {joinUrl}" />{:else}<div class="qrph"></div>{/if}
    </div>
  </section>

  <section class="players">
    <h2>Players <span>{view.players.length}/{MAX_PLAYERS}</span></h2>
    {#if view.players.length === 0}
      <p class="empty">Scan the code to join the party…</p>
    {:else}
      <PlayerList players={view.players} big onkick={kick} />
    {/if}
  </section>

  <section class="settings">
    <div class="row">
      <span class="lbl">Mode</span>
      {#each ['single', 'album'] as const as m}
        <button class="chip" class:on={view.settings.mode === m} onclick={() => setMode(m)}>{m === 'single' ? 'Single' : 'Album'}</button>
      {/each}
      <span class="mode-hint">{view.settings.mode === 'album' ? '3 tracks on one card · most points wins' : 'One game · first line wins'}</span>
    </div>
    <div class="row">
      <span class="lbl">Decades</span>
      {#each DECADES as d}
        <button class="chip" class:on={decades.includes(d)} onclick={() => toggleDecade(d)}>{String(d).slice(2)}s</button>
      {/each}
    </div>
    {#if note}<p class="note">{note}</p>{/if}
  </section>

  <footer>
    <button class="btn-neon start" disabled={!canStart || starting} onclick={onStartClicked}>Start</button>
    {#if albumBlock}
      <span class="hint block" aria-live="polite">{albumBlock}</span>
    {:else}
      <span class="hint" aria-live="polite">{enough ? 'Ready!' : `Need ${MIN_PLAYERS} players to start`}</span>
    {/if}
    {#if startError}<p class="start-error" role="alert">{startError}</p>{/if}
  </footer>
  <div class="tools">
    <HowToPlayLink cls="howto" size="small" label="❓ How to play" backLabel="← Back to lobby" />
    <TvWindowButton size="small" />
  </div>
</main>

<style>
  main { position: relative; min-height: 100%; padding: 2vh 4vw 1.5rem; display: grid; gap: 2vh; align-content: start; justify-items: center; }
  main > section { width: min(100%, 1400px); }
  header { text-align: center; }
  h1 { margin: 0; font-size: clamp(2.5rem, min(6vw, 9vh), 5rem); }
  .join { display: flex; justify-content: center; align-items: center; gap: 4vw; flex-wrap: wrap; }
  .codebox { text-align: center; }
  .label { font-size: clamp(1rem, 2vw, 1.6rem); opacity: 0.85; }
  .code { font-size: clamp(4.5rem, min(15vw, 20vh), 12rem); font-weight: 900; letter-spacing: 0.12em; line-height: 1.05; text-indent: 0.12em; text-shadow: 0 0 10px var(--cyan), 0 0 30px var(--cyan), 0 0 70px var(--cyan); }
  .url { font-size: clamp(1rem, 2vw, 1.6rem); color: var(--cyan); }
  .qr img, .qrph { width: clamp(140px, min(20vw, 28vh), 260px); aspect-ratio: 1; background: #fff; display: block; box-shadow: 0 0 40px rgba(255, 61, 242, 0.5); }
  .players, .settings { text-align: center; }
  .players :global(ul) { justify-content: center; }
  h2 { margin: 0 0 1vh; font-size: clamp(1.2rem, 2.4vw, 2rem); color: var(--cyan); }
  h2 span { opacity: 0.6; }
  .empty { font-size: clamp(1.1rem, 2.2vw, 1.8rem); opacity: 0.6; margin: 0; }
  .settings { display: grid; gap: 1vh; }
  .row { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
  .lbl { min-width: 5.5em; text-align: right; font-weight: 800; font-size: clamp(1rem, 1.8vw, 1.4rem); opacity: 0.8; }
  .chip {
    font: inherit; font-weight: 800; font-size: clamp(1.25rem, 2.2vw, 2rem); color: #fff; cursor: pointer;
    padding: 0.3em 0.9em; border-radius: 999px; background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.2);
  }
  .chip.on { background: rgba(255, 61, 242, 0.3); border-color: var(--pink); box-shadow: 0 0 14px rgba(255, 61, 242, 0.55); }
  .start-error { margin: 0; text-align: center; color: #ffb3f8; font-size: clamp(1.1rem, 1.8vw, 1.5rem); font-weight: 700; }
  .note { margin: 0; color: #ffb3f8; font-size: clamp(1rem, 1.6vw, 1.3rem); }
  .hint.block { opacity: 1; font-weight: 800; font-size: clamp(1.1rem, 1.8vw, 1.5rem); color: #fff; text-shadow: 0 0 8px var(--pink), 0 0 20px var(--pink); }
  .mode-hint { font-size: clamp(0.95rem, 1.5vw, 1.25rem); opacity: 0.7; min-width: 17em; text-align: left; }
  footer { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; }
  .tools { position: absolute; left: 1.5rem; bottom: 1rem; display: flex; align-items: center; gap: 0.6rem; }
  .start { font-size: clamp(1.6rem, 3.6vw, 3rem); padding: 0.4em 2.2em; }
  .hint { opacity: 0.8; font-size: clamp(1.1rem, 1.8vw, 1.4rem); text-align: center; }
  /* 720p TVs: tighter vertical rhythm so Start and its hint stay on screen with notes showing. */
  @media (max-height: 760px) {
    main { gap: 1.2vh; padding-top: 1vh; }
    .code { font-size: clamp(4rem, min(15vw, 16vh), 12rem); }
    .qr img, .qrph { width: clamp(120px, min(20vw, 22vh), 260px); }
    h2 { margin-bottom: 0.5vh; }
    .settings { gap: 0.6vh; }
    .chip { padding: 0.2em 0.9em; }
    footer { gap: 0.3rem; }
    .start { font-size: clamp(1.4rem, 3vw, 2.4rem); padding: 0.3em 2.2em; }
  }
</style>
